import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import DatePicker from "react-datepicker";
import { FloatingLabel, Form, InputGroup } from "react-bootstrap";
import { Column, Row, Select } from "../../../../components";
import { RootState } from "../../../../shared/store";
import { policyAutoVehiclesReadDto } from "../../../model/PolicyVehicleDto";
import { updateSelectedPolicyDetails } from "../../../customer-slice/Customer-slice";
import { CommonUtility } from "../../../../Services/CommonUtility";
import { SelectOption } from "../../../../shared/components/input/SelectOption";
import * as constant from "../../../../shared/util/constant";
import { CustomerBasicVehicleValidation, CustomerVehicleFieldNames } from "../../../../shared/util/constant";
import CustomerBasicVehicleService from "./CustomerBasicVehicleService";

export interface ICustomerBasicVehicleForm {
    customerId?: string;
    vehicleId?: string;
    polId?: string;
    polEventId?: string;
    policyVehicle: policyAutoVehiclesReadDto;
    setIsFormSaved?: (isSaved: boolean) => void;
    formValidation?: any;
    tabIndex?: number;
}

const VEHICLE_CONDITIONS: SelectOption[] = [
    { label: "New", value: "1" },
    { label: "Used", value: "0" },
];

const LEASED_OPTIONS: SelectOption[] = [
    { label: "Yes", value: "1" },
    { label: "No", value: "0" },
];

const CURRENT_YEAR = new Date().getFullYear();

const isLeasedYes = (value: unknown): boolean => {
    return value === true || value === 1 || value === "1" || value === "true";
};

const validateVin = (value: string | undefined) => {
    if (!value || value.trim().length === 0) {
        return constant.FIELD_BLANK;
    }
    if (value.length !== 17) {
        return CustomerBasicVehicleValidation.VIN_LENGTH_ERROR;
    }
    return null;
};

const validateRegisterState = (value: string | undefined) => {
    if (!value) {
        return constant.FIELD_BLANK;
    }
    return null;
};

const validateVehicleYear = (value: string | number | undefined) => {
    if (value === undefined || value === null || value === "") {
        return constant.FIELD_BLANK;
    }
    const num = typeof value === "string" ? parseInt(value, 10) : value;
    if (isNaN(num)) {
        return CustomerBasicVehicleValidation.VEHICLE_YEAR_NUMERIC_ERROR;
    }
    if (num < 1900 || num > CURRENT_YEAR + 1) {
        return CustomerBasicVehicleValidation.VEHICLE_YEAR_RANGE_ERROR(CURRENT_YEAR);
    }
    return null;
};

const validateMake = (value: string | undefined) => {
    if (!value) {
        return constant.FIELD_BLANK;
    }
    return null;
};

const validateModel = (value: string | undefined) => {
    if (!value) {
        return constant.FIELD_BLANK;
    }
    return null;
};

const validateBodyType = (_value: string | undefined) => {
    return null; // Not mandatory
};

const validatePurchaseDate = (value: string | undefined) => {
    if (!value) return null; // Not mandatory
    const trimmed = value.trim();
    const parts = trimmed.split("/");

    if (parts.length !== 2) {
        return CustomerBasicVehicleValidation.PURCHASE_DATE_FORMAT_ERROR;
    }

    const month = parseInt(parts[0], 10);
    const year = parseInt(parts[1], 10);

    if (isNaN(month) || isNaN(year)) {
        return CustomerBasicVehicleValidation.PURCHASE_DATE_NUMERIC_ERROR;
    }

    if (month < 1 || month > 12) {
        return CustomerBasicVehicleValidation.PURCHASE_DATE_MONTH_ERROR;
    }

    if (parts[1].length !== 2) {
        return CustomerBasicVehicleValidation.PURCHASE_DATE_FORMAT_ERROR;
    }

    const now = new Date();
    const currentYr = now.getFullYear() % 100;
    const currentMonth = now.getMonth() + 1;
    if (year > currentYr || (year === currentYr && month > currentMonth)) {
        return CustomerBasicVehicleValidation.PURCHASE_DATE_FUTURE_ERROR;
    }

    return null;
};

const validateLeasedDate = (value: string | undefined) => {
    if (!value) return null; // Not mandatory
    const parts = value.split("/");
    if (parts.length !== 3) {
        return CustomerBasicVehicleValidation.LEASED_DATE_FORMAT_ERROR;
    }
    const month = parseInt(parts[0], 10);
    const day = parseInt(parts[1], 10);
    const year = parseInt(parts[2], 10);
    if (isNaN(month) || isNaN(day) || isNaN(year)) {
        return CustomerBasicVehicleValidation.LEASED_DATE_NUMERIC_ERROR;
    }
    if (month < 1 || month > 12) {
        return CustomerBasicVehicleValidation.PURCHASE_DATE_MONTH_ERROR;
    }
    if (day < 1 || day > 31) {
        return CustomerBasicVehicleValidation.LEASED_DATE_DAY_ERROR;
    }
    if (parts[2].length !== 4 || year < 1900) {
        return CustomerBasicVehicleValidation.LEASED_DATE_YEAR_ERROR;
    }
    const inputDate = new Date(year, month - 1, day);
    if (inputDate > new Date()) {
        return CustomerBasicVehicleValidation.LEASED_DATE_FUTURE_ERROR;
    }
    return null;
};

const validateCostNew = (value: string | number | undefined) => {
    if (!value && value !== 0) return null; // Not mandatory
    const num = typeof value === "string" ? parseInt(value, 10) : value;
    if (isNaN(num)) {
        return CustomerBasicVehicleValidation.COST_NEW_NUMERIC_ERROR;
    }
    if (num < 0) {
        return CustomerBasicVehicleValidation.COST_NEW_NEGATIVE_ERROR;
    }
    return null;
};

const CustomerBasicVehicleForm: React.FunctionComponent<ICustomerBasicVehicleForm> = (props) => {
    const { policyVehicle, setIsFormSaved, vehicleId, polId, polEventId, formValidation: externalFormValidation } = props;
    let tabIndex = props.tabIndex;
    const dispatch = useDispatch();
    const customerDetail = useSelector((state: RootState) => state.customerSlice);
    const policyDetailKey = CommonUtility.getPolicyDetailKey(polId ?? "", polEventId ?? "");
    const policyDetails = customerDetail.selectedPolicyDetails?.[policyDetailKey];
    const stateUSLookup = useSelector((state: RootState) => state?.LookupState.stateUSList);
    const [states, setStates] = useState<SelectOption[]>([]);
    const vehicleService = new CustomerBasicVehicleService();
    const [vehicleData, setVehicleData] = useState<policyAutoVehiclesReadDto>(policyVehicle ?? ({} as policyAutoVehiclesReadDto));
    const [formValidation, setFormValidation] = useState<any>(externalFormValidation || {});

    // Helper function to clean vehicle data and remove duplicate PascalCase properties
    const cleanVehicleData = (data: any): policyAutoVehiclesReadDto => {
        // Helper to safely parse costNew
        const parseCostNew = (value: any): number | undefined => {
            if (value == null) return undefined;
            const num = typeof value === "string" ? parseInt(value, 10) : value;
            return isNaN(num) ? undefined : num;
        };

        const cleaned: policyAutoVehiclesReadDto = {
            id: data.id,
            polId: data.polId,
            polEventId: data.polEventId,
            lobId: data.lobId,
            vehicleNumber: data.vehicleNumber,
            vin: data.vin,
            registerState: data.registerState,
            vehicleYear: data.vehicleYear,
            make: data.make,
            model: data.model,
            bodyType: data.bodyType,
            isVehicleNew: data.isVehicleNew,
            isLeased: data.isLeased,
            purchaseDate: data.purchaseDate,
            leasedDate: data.leasedDate,
            costNew: parseCostNew(data.costNew),
            effDate: data.effDate,
        };
        return cleaned;
    };

    useEffect(() => {
        // States are already loaded centrally in CustomerEditPolicyDetails
        // and available in Redux store - no need to fetch here
        if (stateUSLookup && stateUSLookup.length > 0) {
            setStates([...stateUSLookup]);
        }
    }, [stateUSLookup]);

    useEffect(() => {
        if (policyDetails && policyDetails.policyLineOfBusinesses) {
            const lob = policyDetails.policyLineOfBusinesses.find((l) => l.polEventId === polEventId);
            if (lob && lob.policyAutoVehicles) {
                const vehicle = lob.policyAutoVehicles.find((v) => v.id === vehicleId);
                if (vehicle) {
                    setVehicleData(vehicle);
                } else {
                }
            } else {
            }
        } else {
        }
    }, [vehicleId, polEventId, policyDetails]);

    useEffect(() => {
        // Sync parent validation errors (from Save action) to local form state
        if (externalFormValidation) {
            setFormValidation(externalFormValidation);
        }
    }, [externalFormValidation]);

    const syncVehicleData = (updatedData: policyAutoVehiclesReadDto) => {
        setVehicleData(updatedData);

        const updatedPolicyDetails = JSON.parse(JSON.stringify(policyDetails));
        if (updatedPolicyDetails && updatedPolicyDetails.policyLineOfBusinesses) {
            const updatedLOBs = updatedPolicyDetails.policyLineOfBusinesses.map((lob: any) => {
                if (lob.polEventId === polEventId) {
                    const vehicles: policyAutoVehiclesReadDto[] = lob.policyAutoVehicles ?? [];
                    const vehicleExists = vehicles.some((v) => v.id === vehicleId);
                    if (vehicleExists) {
                        return {
                            ...lob,
                            policyAutoVehicles: vehicles.map((v) =>
                                v.id === vehicleId ? cleanVehicleData({ ...v, ...updatedData }) : v
                            ),
                        };
                    } else {
                        return {
                            ...lob,
                            policyAutoVehicles: [cleanVehicleData({ ...updatedData, id: vehicleId }), ...vehicles],
                        };
                    }
                }
                return lob;
            });
            updatedPolicyDetails.policyLineOfBusinesses = updatedLOBs;
            dispatch(
                updateSelectedPolicyDetails({
                    policyId: polId ?? "",
                    polEventId: polEventId ?? "",
                    policyDetails: updatedPolicyDetails,
                })
            );
        } 

        if (setIsFormSaved) setIsFormSaved(false);
    };

    const onFieldChange = (propName: string, value: string | number | boolean | null) => {
        const updatedData = { ...vehicleData, [propName]: value } as policyAutoVehiclesReadDto;
        syncVehicleData(updatedData);
    };

    const handleVinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const cleaned = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 17);
        onFieldChange(CustomerVehicleFieldNames.VIN, cleaned);
    };

    const handleFieldBlur = (fieldName: string, value: any) => {
        let errorMsg = null;
        switch (fieldName) {
            case CustomerVehicleFieldNames.VIN:
                errorMsg = validateVin(value);
                break;
            case CustomerVehicleFieldNames.REGISTER_STATE:
                errorMsg = validateRegisterState(value);
                break;
            case CustomerVehicleFieldNames.VEHICLE_YEAR:
                errorMsg = validateVehicleYear(value);
                break;
            case CustomerVehicleFieldNames.MAKE:
                errorMsg = validateMake(value);
                break;
            case CustomerVehicleFieldNames.MODEL:
                errorMsg = validateModel(value);
                break;
            case CustomerVehicleFieldNames.BODY_TYPE:
                errorMsg = validateBodyType(value);
                break;
            case CustomerVehicleFieldNames.PURCHASE_DATE:
                errorMsg = validatePurchaseDate(value);
                break;
            case CustomerVehicleFieldNames.COST_NEW:
                errorMsg = validateCostNew(value);
                break;
            case CustomerVehicleFieldNames.LEASED_DATE:
                if (isLeasedYes(vehicleData.isLeased) && (!value || String(value).trim().length === 0)) {
                    errorMsg = CustomerBasicVehicleValidation.LEASED_DATE_REQUIRED_ERROR;
                } else {
                    errorMsg = validateLeasedDate(
                        value ? CommonUtility.formateDateStringtoDate(value) : undefined
                    );
                }
                break;
        }
        
        if (errorMsg) {
            setFormValidation((prev: any) => ({
                ...prev,
                [`${fieldName}_${vehicleId}`]: { isValid: false, warningText: errorMsg },
            }));
        } else {
            setFormValidation((prev: any) => {
                const next = { ...prev };
                delete next[`${fieldName}_${vehicleId}`];
                return next;
            });
        }
    };

    const handleYearChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        if (val === "") {
            onFieldChange(CustomerVehicleFieldNames.VEHICLE_YEAR, null);
            return;
        }
        const num = parseInt(val, 10);
        onFieldChange(CustomerVehicleFieldNames.VEHICLE_YEAR, isNaN(num) ? null : String(num));
    };

    const handleMmYyChange = (propName: string, raw: string) => {
        const digits = raw.replace(/\D/g, "").slice(0, 4);
        let formatted = digits;
        if (digits.length > 2) {
            formatted = `${digits.slice(0, 2)}/${digits.slice(2)}`;
        }
        onFieldChange(propName, formatted);
    };

    const handleLeasedDateChange = (date: Date | null) => {
        if (!date) {
            onFieldChange(CustomerVehicleFieldNames.LEASED_DATE, null);
            setFormValidation((prev: any) => {
                const next = { ...prev };
                delete next[`leasedDate_${vehicleId}`];
                return next;
            });
            return;
        }
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        const year = date.getFullYear();
        onFieldChange(CustomerVehicleFieldNames.LEASED_DATE, `${month}/${day}/${year}`);
        if (date > new Date()) {
            setFormValidation((prev: any) => ({
                ...prev,
                [`leasedDate_${vehicleId}`]: { isValid: false, warningText: CustomerBasicVehicleValidation.LEASED_DATE_FUTURE_ERROR },
            }));
        } else {
            setFormValidation((prev: any) => {
                const next = { ...prev };
                delete next[`leasedDate_${vehicleId}`];
                return next;
            });
        }
    };

    const handleIsLeasedChange = (values: string[]) => {
        const selectedValue = values && values.length > 0 ? values[0] : undefined;
        const leasedFlag = selectedValue === "1" ? true : selectedValue === "0" ? false : null;

        if (leasedFlag === false) {
            // Update both fields together to avoid stale state overwriting isLeased.
            syncVehicleData({
                ...vehicleData,
                isLeased: leasedFlag,
                leasedDate: null,
            } as policyAutoVehiclesReadDto);
            setFormValidation((prev: any) => {
                const next = { ...prev };
                delete next[`leasedDate_${vehicleId}`];
                return next;
            });
            return;
        }

        onFieldChange(CustomerVehicleFieldNames.IS_LEASED, leasedFlag);
    };

    const handleCostNewChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value.trim();
        if (val === "") {
            onFieldChange(CustomerVehicleFieldNames.COST_NEW, null);
            return;
        }
        // Remove non-numeric characters and parse (integers only)
        const cleaned = val.replace(/[^0-9]/g, "");
        if (cleaned === "") {
            onFieldChange(CustomerVehicleFieldNames.COST_NEW, null);
            return;
        }
        const num = parseInt(cleaned, 10);
        // Validate: no negatives
        if (num < 0) {
            return; // Silently ignore negative input
        }
        onFieldChange(CustomerVehicleFieldNames.COST_NEW, num);
    };

    return (
        <div className="main-policy-location-div">
            {/* Row 1: Vehicle Number + VIN + Registration State */}
            <Row>
                <Column lg={12} md={12}>
                    <Row className="mb-3">
                        <Column lg={4} md={4}>
                            <FloatingLabel controlId="vehicleNumber" label="Vehicle Number">
                                <Form.Control
                                    type="number"
                                    name="vehicleNumber"
                                    value={vehicleData.vehicleNumber ?? ""}
                                    placeholder="Vehicle Number"
                                    tabIndex={tabIndex}
                                    disabled={true}
                                    readOnly
                                />
                            </FloatingLabel>
                        </Column>
                        <Column lg={4} md={4}
                            className={formValidation[`vin_${vehicleId}`]?.isValid === false ? "feedback-row" : ""}>
                            <FloatingLabel controlId="vin" label="VIN">
                                <Form.Control
                                    type="text"
                                    name="vin"
                                    value={vehicleData.vin ?? ""}
                                    onChange={handleVinChange}
                                    onBlur={() => handleFieldBlur(CustomerVehicleFieldNames.VIN, vehicleData.vin)}
                                    placeholder={CustomerBasicVehicleValidation.VIN_PLACEHOLDER}
                                    tabIndex={tabIndex}
                                    maxLength={17}
                                    isInvalid={formValidation[`vin_${vehicleId}`]?.isValid === false}
                                />
                                {formValidation[`vin_${vehicleId}`]?.isValid === false && (
                                    <Form.Control.Feedback type="invalid">
                                        {formValidation[`vin_${vehicleId}`]?.warningText}
                                    </Form.Control.Feedback>
                                )}
                            </FloatingLabel>
                        </Column>
                        <Column lg={4} md={4}
                            className={formValidation[`registerState_${vehicleId}`]?.isValid === false ? "feedback-row" : ""}>
                            <Select
                                id="registrationState"
                                controlId="registrationState"
                                label="Register State *"
                                options={states}
                                defaultSelected={vehicleData.registerState ?
                                    states.filter(option => option.value === vehicleData.registerState) :
                                    []
                                }
                                onChange={(values) => {
                                    const selectedState = values && values.length > 0 ? (values[0] as string) : undefined;
                                    onFieldChange(CustomerVehicleFieldNames.REGISTER_STATE, selectedState ?? null);
                                    handleFieldBlur(CustomerVehicleFieldNames.REGISTER_STATE, selectedState);
                                }}
                                onBlur={() => handleFieldBlur(CustomerVehicleFieldNames.REGISTER_STATE, vehicleData.registerState)}
                                className="mb-3"
                                tabIndex={tabIndex}
                                isInvalid={formValidation[`registerState_${vehicleId}`]?.isValid === false}
                                invalidWarning={formValidation[`registerState_${vehicleId}`]?.warningText}
                            />
                        </Column>
                    </Row>

                    {/* Row 2: Year + Make + Model */}
                    <Row className="mb-3">
                        <Column lg={4} md={4}
                            className={formValidation[`vehicleYear_${vehicleId}`]?.isValid === false ? "feedback-row" : ""}>
                            <FloatingLabel controlId="year" label="Year *">
                                <Form.Control
                                    type="text"
                                    name="year"
                                    value={vehicleData.vehicleYear ?? ""}
                                    onChange={handleYearChange}
                                    onBlur={() => handleFieldBlur(CustomerVehicleFieldNames.VEHICLE_YEAR, vehicleData.vehicleYear)}
                                    placeholder="Year"
                                    tabIndex={tabIndex}
                                    isInvalid={formValidation[`vehicleYear_${vehicleId}`]?.isValid === false}
                                />
                                {formValidation[`vehicleYear_${vehicleId}`]?.isValid === false && (
                                    <Form.Control.Feedback type="invalid">
                                        {formValidation[`vehicleYear_${vehicleId}`]?.warningText}
                                    </Form.Control.Feedback>
                                )}
                            </FloatingLabel>
                        </Column>
                        <Column lg={4} md={4}
                            className={formValidation[`make_${vehicleId}`]?.isValid === false ? "feedback-row" : ""}>
                            <FloatingLabel controlId="make" label="Make *">
                                <Form.Control
                                    type="text"
                                    name="make"
                                    value={vehicleData.make ?? ""}
                                    onChange={(e) => onFieldChange(CustomerVehicleFieldNames.MAKE, e.target.value)}
                                    onBlur={() => handleFieldBlur(CustomerVehicleFieldNames.MAKE, vehicleData.make)}
                                    placeholder="Make"
                                    tabIndex={tabIndex}
                                    isInvalid={formValidation[`make_${vehicleId}`]?.isValid === false}
                                />
                                {formValidation[`make_${vehicleId}`]?.isValid === false && (
                                    <Form.Control.Feedback type="invalid">
                                        {formValidation[`make_${vehicleId}`]?.warningText}
                                    </Form.Control.Feedback>
                                )}
                            </FloatingLabel>
                        </Column>
                        <Column lg={4} md={4}
                            className={formValidation[`model_${vehicleId}`]?.isValid === false ? "feedback-row" : ""}>
                            <FloatingLabel controlId="model" label="Model *">
                                <Form.Control
                                    type="text"
                                    name="model"
                                    value={vehicleData.model ?? ""}
                                    onChange={(e) => onFieldChange(CustomerVehicleFieldNames.MODEL, e.target.value)}
                                    onBlur={() => handleFieldBlur(CustomerVehicleFieldNames.MODEL, vehicleData.model)}
                                    placeholder="Model"
                                    tabIndex={tabIndex}
                                    isInvalid={formValidation[`model_${vehicleId}`]?.isValid === false}
                                />
                                {formValidation[`model_${vehicleId}`]?.isValid === false && (
                                    <Form.Control.Feedback type="invalid">
                                        {formValidation[`model_${vehicleId}`]?.warningText}
                                    </Form.Control.Feedback>
                                )}
                            </FloatingLabel>
                        </Column>
                    </Row>

                    {/* Row 3: Body Style + Condition + Purchase MM/YY */}
                    <Row className="mb-3">
                        <Column lg={4} md={4}>
                            <FloatingLabel controlId="bodyType" label="Body Style">
                                <Form.Control
                                    type="text"
                                    name="bodyType"
                                    value={vehicleData.bodyType ?? ""}
                                    onChange={(e) => onFieldChange(CustomerVehicleFieldNames.BODY_TYPE, e.target.value)}
                                    placeholder="Body Type"
                                    tabIndex={tabIndex}
                                />
                            </FloatingLabel>
                        </Column>
                        <Column lg={4} md={4}>
                            <Select
                                id="condition"
                                controlId="condition"
                                label="Condition"
                                options={VEHICLE_CONDITIONS}
                                defaultSelected={
                                    vehicleData.isVehicleNew != null
                                        ? VEHICLE_CONDITIONS.filter((o) => o.value === (vehicleData.isVehicleNew === true || vehicleData.isVehicleNew === "1" ? "1" : "0"))
                                        : []
                                }
                                onChange={(values) => {
                                    if (!values || values.length === 0) {
                                        onFieldChange("isVehicleNew", null);
                                    } else {
                                        const boolValue = values[0] === "1" ? true : false;
                                        onFieldChange("isVehicleNew", boolValue);
                                    }
                                }}
                                className="mb-3"
                                tabIndex={tabIndex}
                            />
                        </Column>
                        <Column lg={4} md={4}
                            className={formValidation[`purchaseDate_${vehicleId}`]?.isValid === false ? "feedback-row" : ""}>
                            <FloatingLabel controlId="purchaseMmYy" label="Purchase Date (MM/YY)">
                                <Form.Control
                                    type="text"
                                    name="purchaseMmYy"
                                    value={vehicleData.purchaseDate ?? ""}
                                    onChange={(e) => handleMmYyChange(CustomerVehicleFieldNames.PURCHASE_DATE, e.target.value)}
                                    onBlur={() => handleFieldBlur(CustomerVehicleFieldNames.PURCHASE_DATE, vehicleData.purchaseDate)}
                                    placeholder={CustomerBasicVehicleValidation.PURCHASE_DATE_PLACEHOLDER}
                                    tabIndex={tabIndex}
                                    maxLength={5}
                                    isInvalid={formValidation[`purchaseDate_${vehicleId}`]?.isValid === false}
                                />
                                {formValidation[`purchaseDate_${vehicleId}`]?.isValid === false && (
                                    <Form.Control.Feedback type="invalid">
                                        {formValidation[`purchaseDate_${vehicleId}`]?.warningText}
                                    </Form.Control.Feedback>
                                )}
                            </FloatingLabel>
                        </Column>
                    </Row>

                    {/* Row 4: Is Leased + Leased Date + Cost New */}
                    <Row className="mb-3">
                        <Column lg={4} md={4}>
                            <Select
                                id="isLeased"
                                controlId="isLeased"
                                label="Is Leased"
                                options={LEASED_OPTIONS}
                                defaultSelected={
                                    vehicleData.isLeased !== undefined && vehicleData.isLeased !== null
                                        ? LEASED_OPTIONS.filter((o) => o.value === (isLeasedYes(vehicleData.isLeased) ? "1" : "0"))
                                        : []
                                }
                                onChange={handleIsLeasedChange}
                                className="mb-3"
                                tabIndex={tabIndex}
                            />
                        </Column>
                        {isLeasedYes(vehicleData.isLeased) && (
                        <Column lg={4} md={4}
                            className={formValidation[`leasedDate_${vehicleId}`]?.isValid === false ? "feedback-row" : ""}>
                            <InputGroup className="mb-3">
                                <InputGroup.Text>Leased Date *</InputGroup.Text>
                                <DatePicker
                                    dateFormatCalendar="LLLL yyyy"
                                    tabIndex={tabIndex}
                                    dropdownMode="scroll"
                                    selected={vehicleData.leasedDate ? new Date(vehicleData.leasedDate) : null}
                                    onChange={handleLeasedDateChange}
                                    onBlur={() => handleFieldBlur(CustomerVehicleFieldNames.LEASED_DATE, vehicleData.leasedDate)}
                                    className={`form-control${formValidation[`leasedDate_${vehicleId}`]?.isValid === false ? ' is-invalid' : ''}`}
                                />
                                <InputGroup.Text>
                                    <i className="bi bi-calendar3"></i>
                                </InputGroup.Text>
                                {formValidation[`leasedDate_${vehicleId}`]?.isValid === false && (
                                    <div className="invalid-feedback" style={{ display: "block" }}>
                                        {formValidation[`leasedDate_${vehicleId}`]?.warningText}
                                    </div>
                                )}
                            </InputGroup>
                        </Column>
                        )}
                        <Column lg={4} md={4}
                            className={formValidation[`costNew_${vehicleId}`]?.isValid === false ? "feedback-row" : ""}>
                            <FloatingLabel controlId="costNew" label="Cost New ($)">
                                <Form.Control
                                    type="text"
                                    name="costNew"
                                    value={typeof vehicleData.costNew === "number" ? String(vehicleData.costNew) : ""}
                                    onChange={handleCostNewChange}
                                    onBlur={() => handleFieldBlur(CustomerVehicleFieldNames.COST_NEW, vehicleData.costNew)}
                                    placeholder="Cost New"
                                    tabIndex={tabIndex}
                                    isInvalid={formValidation[`costNew_${vehicleId}`]?.isValid === false}
                                />
                                {formValidation[`costNew_${vehicleId}`]?.isValid === false && (
                                    <Form.Control.Feedback type="invalid">
                                        {formValidation[`costNew_${vehicleId}`]?.warningText}
                                    </Form.Control.Feedback>
                                )}
                            </FloatingLabel>
                        </Column>
                    </Row>
                </Column>
            </Row>
        </div>
    );
};

export default CustomerBasicVehicleForm;
